import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import { useTravlData } from './TravlContext';
import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';

const TIME_SLOTS = Array.from({ length: 25 }, (_, i) =>
    `${i.toString().padStart(2, '0')}:00`
);

export const TRAVL_MODE_DESC: string[] = [
    'Bus',
    'Car',
    'All Car Drivers',
    'Car Driver (alone)',
    'Car Driver (with pass)',
    'Car Passenger',
    'Coach',
    'DLR',
    'HGV',
    'Motor Cycle',
    'Other',
    'Park and Ride Bus',
    'Pedal Cycle',
    'Rail',
    'River Boat',
    'Taxi',
    'Taxi Occupants',
    'Tram',
    'Underground',
    'Unknown',
    'Walk/PT',
    'Walk'
];

const modeCodeMap: Record<string, string> = {
    BUS: "Bus",
    CAR: "Car",
    CARA: "All Car Drivers",
    CARD: "Car Driver (alone)",
    CARM: "Car Driver (with pass)",
    CARP: "Car Passenger",
    COAC: "Coach",
    DLR: "DLR",
    HGV: "HGV",
    MOTC: "Motor Cycle",
    OTHR: "Other",
    PARB: "Park and Ride Bus",
    PEDC: "Pedal Cycle",
    RAIL: "Rail",
    RBOT: "River Boat",
    TAXI: "Taxi",
    TAXO: "Taxi Occupants",
    TRAM: "Tram",
    TUBE: "Underground",
    UNKW: "Unknown",
    WALK: "Walk/PT",
    WALO: "Walk"
};

type TableRowType = {
    SurveyCode: string;
} & {
    [key: string]: number | string; // allow mode fields and SurveyCode
};

interface IProps {
    direction: string;
}

export default function AllTravlSurveysTable({ direction }: IProps) {
    const { surveyData, exportToCsv } = useTravlData();
    const [rows, setRows] = useState<TableRowType[]>([]);
    const [timeRanges, setTimeRanges] = useState<Record<string, { start: string, end: string }>>({});
    const [selected, setSelected] = useState<Record<string, boolean>>({});



    useEffect(() => {
        const codes = surveyData.map(e => e.SurveyCode);
        setSelected(sel =>
            codes.reduce((acc, code) => {
                acc[code] = sel[code] ?? true;
                return acc;
            }, {} as Record<string, boolean>)
        );
    }, [surveyData]);

    useEffect(() => {
        const grouped = surveyData.reduce((acc, entry) => {
            const code = entry.SurveyCode;
            if (!acc[code]) acc[code] = [];
            acc[code].push(entry);
            return acc;
        }, {} as Record<string, typeof surveyData>);

        const rows1 = Object.entries(grouped).map(([surveyCode, entries]) => {
            const timeRange = timeRanges[surveyCode] || { start: "00:00", end: "24:00" };
            const filtered = entries.filter(e =>
                (!e.TimeBand || (e.TimeBand >= timeRange.start && e.TimeBand <= timeRange.end))
            );

            const modeCounts: Record<string, number> = {};
            const isArrival = direction === 'Arrivals';
            const isDeparture = direction === 'Departures';

            const getValue = (e) => {
                const isOcc = e.ModeCode === 'CARP' || e.ModeCode === 'TAXO';
                const inField = isOcc ? 'OccIn' : 'JnysIn';
                const outField = isOcc ? 'OccOut' : 'JnysOut';

                if (isArrival) return parseInt(e[inField] || "0", 10);
                if (isDeparture) return parseInt(e[outField] || "0", 10);
                return (parseInt(e[inField] || "0", 10) + parseInt(e[outField] || "0", 10));
            };

            for (const e of filtered) {
                const desc = modeCodeMap[e.ModeCode] || e.ModeCode;
                const val = getValue(e);
                modeCounts[desc] = (modeCounts[desc] || 0) + val;
            }


            return {
                SurveyCode: surveyCode,
                ...TRAVL_MODE_DESC.reduce((acc, mode) => ({
                    ...acc,
                    [mode]: modeCounts[mode] || 0
                }), {})
            };
        });

        setRows(rows1);
    }, [surveyData, timeRanges]);


    return (
        <>
            <Button onClick={() => exportToCsv(rows)} >Export All</Button>
            <TableContainer component={Paper}>
                <Table size={'small'} sx={{
                    'td, th': {
                        padding: '4px',
                        fontSize: '0.73rem',
                        whiteSpace: 'nowrap',
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }
                }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={Object.values(selected).every(Boolean)}
                                    indeterminate={
                                        Object.values(selected).some(Boolean) && !Object.values(selected).every(Boolean)
                                    }
                                    onChange={e => {
                                        const checked = e.target.checked;
                                        setSelected(sel => {
                                            const allCodes = rows.map(r => r.SurveyCode);
                                            return allCodes.reduce((acc, code) => {
                                                acc[code] = checked;
                                                return acc;
                                            }, {} as Record<string, boolean>);
                                        });
                                    }}
                                    size="medium"
                                    sx={{ padding: 0 }}
                                />
                            </TableCell>
                            <TableCell>SurveyCode</TableCell>
                            <TableCell />
                            <TableCell />
                            {TRAVL_MODE_DESC.map((mode, i) => (
                                <TableCell key={i} align="right">{mode}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => (
                            <TableRow key={idx}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selected[row.SurveyCode] ?? true}
                                        onChange={e =>
                                            setSelected(sel => ({
                                                ...sel,
                                                [row.SurveyCode]: e.target.checked
                                            }))
                                        }
                                        size="medium"
                                        sx={{ padding: 0 }}
                                    />
                                </TableCell>
                                <TableCell>{row.SurveyCode}</TableCell>
                                <TableCell>
                                    <Select

                                        value={timeRanges[row.SurveyCode]?.start || '00:00'}
                                        onChange={e =>
                                            setTimeRanges(r => ({
                                                ...r,
                                                [row.SurveyCode]: {
                                                    ...(r[row.SurveyCode] || { start: '00:00', end: '24:00' }),
                                                    start: e.target.value as string
                                                }
                                            }))
                                        }
                                        size="small"
                                    >
                                        {TIME_SLOTS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                </TableCell>
                                <TableCell>
                                    <Select
                                        value={timeRanges[row.SurveyCode]?.end || '24:00'}
                                        onChange={e =>
                                            setTimeRanges(r => ({
                                                ...r,
                                                [row.SurveyCode]: {
                                                    ...(r[row.SurveyCode] || { start: '00:00', end: '24:00' }),
                                                    end: e.target.value as string
                                                }
                                            }))
                                        }
                                        size="small"
                                    >
                                        {TIME_SLOTS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                </TableCell>
                                {TRAVL_MODE_DESC.map((mode, i) => (
                                    <TableCell key={i} align="right">
                                        {row[mode] || ''}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}
